import { Suspense, lazy } from 'react'
import { Footer, Navigation } from '@/ui/design-system'
import { Logo } from '@/ui/components'
import { Landing3DHero } from '@/ui/components/landing/hero/Landing3DHero'
import { CollaborationSpotlight } from '@/ui/components/landing/sections/CollaborationSpotlight'
import { FeaturesStrip } from '@/ui/components/landing/sections/FeaturesStrip'
import { PricingCompact } from '@/ui/components/landing/sections/PricingCompact'
import { FinalCTA } from '@/ui/components/landing/sections/FinalCTA'

const TripBuilderSection = lazy(() => import('@/ui/components/landing/wizard/TripBuilderSection'))

function WizardSkeleton() {
  return (
    <div id="trip-builder" className="scroll-mt-16 bg-landing-base py-20 lg:py-28" aria-hidden>
      <div className="mx-auto max-w-app px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 h-10 w-72 max-w-full animate-pulse rounded-lg bg-white/5" />
        <div className="h-[560px] animate-pulse rounded-2xl border border-landing-line bg-landing-surface" />
      </div>
    </div>
  )
}

export default function LandingPage() {
  const navigationItems = [
    { title: 'Home', href: '/' },
    { title: 'About', href: '/about' },
  ]

  const navigationActions = {
    primary: { label: 'Get started', href: '/signup' },
    secondary: { label: 'Sign in', href: '/login' },
  }

  const footerSections = [
    {
      title: 'Product',
      links: [
        { label: 'About Us', href: '/about' },
        { label: 'Pricing', href: '/pricing' },
        { label: 'Sign Up', href: '/signup' },
      ],
    },
    {
      title: 'Support',
      links: [
        { label: 'Documentation', href: '/docs' },
        { label: 'Contact Us', href: '/contact' },
        { label: 'Report Issue', href: '/contact' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Terms of Service', href: '/terms' },
        { label: 'Privacy Policy', href: '/privacy' },
      ],
    },
  ]

  return (
    // `.dark` scopes every dark: variant + inverted token to this page only,
    // independent of the app theme toggle on <html>
    <div className="dark landing-theme min-h-screen bg-landing-base text-white">
      <Navigation
        appearance="dark-glass"
        logo={
          <div className="flex items-center gap-2">
            <Logo to="/" size="md" />
            <span className="text-lg font-semibold text-white">SitRepp.io</span>
          </div>
        }
        items={navigationItems}
        actions={navigationActions}
      />

      {/* Cinematic hero + problem strip */}
      <Landing3DHero />

      {/* The centerpiece: interactive trip-builder wizard */}
      <Suspense fallback={<WizardSkeleton />}>
        <TripBuilderSection />
      </Suspense>

      {/* Sharing on your terms — the differentiator */}
      <CollaborationSpotlight />

      {/* Compact feature manifest */}
      <FeaturesStrip />

      {/* Pricing */}
      <PricingCompact />

      {/* Final CTA */}
      <FinalCTA />

      <Footer
        sections={footerSections}
        description="Trip planning for overlanding crews: routes, meals, gear, and weather in one workspace, shared on your terms."
      />
    </div>
  )
}
